import { useState } from "react";
import { AlertTriangle, CalendarX } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Inhabitant } from "@/types/inhabitants";

const CONFIRM_PHRASE = "usuń trwale";

interface Props {
  inhabitant: Inhabitant;
  loading: boolean;
  onClose: () => void;
  onEndContract: () => void;
  onDelete: () => void;
}

export function DeleteInhabitantDialog({ inhabitant, loading, onClose, onEndContract, onDelete }: Props) {
  const [phrase, setPhrase] = useState("");
  const confirmed = phrase === CONFIRM_PHRASE;

  return (
    <Modal
      open
      onClose={onClose}
      title="Usuń mieszkańca"
      size="md"
      footer={
        <div className="flex items-center justify-between w-full gap-2">
          <Button variant="secondary" iconLeft={<CalendarX className="size-3.5" />}
                  onClick={onEndContract} disabled={loading}>
            Zakończ umowę zamiast
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={loading}>Anuluj</Button>
            <Button variant="danger" disabled={!confirmed || loading} loading={loading}
                    onClick={onDelete}>
              Usuń trwale
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Warning banner */}
        <div className="flex gap-3 rounded border border-warning/40 bg-warning/10 px-3 py-3 text-sm">
          <AlertTriangle className="size-4 shrink-0 mt-0.5 text-warning" />
          <div className="flex flex-col gap-1">
            <p className="font-medium text-warning">Rozważ zakończenie umowy zamiast usuwania</p>
            <p className="text-text-muted text-xs">
              Oznaczenie umowy jako zakończonej (przycisk po lewej) zachowuje historię w systemie
              i jest nieodwracalne tylko w sensie czasu. Trwałe usunięcie usuwa wszystkie wersje
              rekordu i nie może być cofnięte.
            </p>
          </div>
        </div>

        {/* Subject */}
        <div className="text-sm">
          <span className="text-text-muted">Mieszkaniec: </span>
          <span className="font-semibold">{inhabitant.name}</span>
          <span className="text-text-muted ml-2">({inhabitant.location}, umowa {inhabitant.agreementId})</span>
        </div>

        {/* Type-to-confirm */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-text-muted">
            Wpisz <span className="font-mono font-semibold text-danger">{CONFIRM_PHRASE}</span> aby odblokować usunięcie:
          </label>
          <Input
            id="delete-confirm"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            autoComplete="off"
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
}
